import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { DemTypeahead, type DemTypeaheadOption } from '../../components/dem/DemTypeahead';
import { useDemBlanketOrdersRealtime } from '../../hooks/useDemBlanketOrdersRealtime';
import {
  fetchBlanketOrder,
  getBlanketPoAttachmentSignedUrl,
  removeBlanketPoAttachmentFile,
  setBlanketOrderStatus,
  setBlanketPoAttachments,
  updateBlanketOrder,
  uploadBlanketPoAttachment,
} from '../../lib/demBlanketOrders';
import { formatDemCustomerAddressDisplay } from '../../lib/demCustomers';
import { loadPoMappingContext } from '../../lib/demPoMapping';
import type { DemBlanketAttachmentRef, DemBlanketOrder, DemCustomer } from '../../types/dem';

type LineDraft = {
  key: string;
  id?: string;
  itemCode: string;
  description: string;
  packing: string;
  qty: string;
  uom: string;
  deliveredQty: number;
};

function formatQty(n: number): string {
  if (!Number.isFinite(n)) return '—';
  return Number.isInteger(n)
    ? String(n)
    : n.toLocaleString('en-SG', { maximumFractionDigits: 2 });
}

function formatWhen(iso: string): string {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat('en-SG', {
      timeZone: 'Asia/Singapore',
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function CollapseSection({
  title,
  open,
  onToggle,
  children,
}: {
  title: string;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  return (
    <section className={`delivery-form-collapse${open ? ' is-open' : ''}`}>
      <button
        type="button"
        className="delivery-form-collapse__toggle"
        aria-expanded={open}
        onClick={onToggle}
      >
        <span>{title}</span>
        <span aria-hidden>{open ? '▾' : '▸'}</span>
      </button>
      {open ? <div className="delivery-form-collapse__body">{children}</div> : null}
    </section>
  );
}

function newDraftLine(): LineDraft {
  return {
    key: `new-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    itemCode: '',
    description: '',
    packing: '',
    qty: '',
    uom: 'PCS',
    deliveredQty: 0,
  };
}

function linesFromOrder(
  order: DemBlanketOrder,
  packingByCode: Map<string, string>,
): LineDraft[] {
  const rows = order.lines ?? [];
  if (rows.length === 0) return [newDraftLine()];
  return rows.map((line) => ({
    key: line.id,
    id: line.id,
    itemCode: line.item_code,
    description: line.description,
    packing: packingByCode.get(line.item_code.trim().toUpperCase()) ?? '',
    qty: String(line.ordered_qty),
    uom: line.uom || 'PCS',
    deliveredQty: line.delivered_qty,
  }));
}

export function DemBlanketOrdersDetailPage() {
  const { id = '' } = useParams();
  const [order, setOrder] = useState<DemBlanketOrder | null>(null);
  const [customers, setCustomers] = useState<DemCustomer[]>([]);
  const [itemOptions, setItemOptions] = useState<DemTypeaheadOption[]>([]);
  const [packingByCode, setPackingByCode] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(true);
  const [ctxReady, setCtxReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [headerOpen, setHeaderOpen] = useState(true);
  const [linesOpen, setLinesOpen] = useState(true);

  const [customerCode, setCustomerCode] = useState('');
  const [poNumber, setPoNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [attachments, setAttachments] = useState<DemBlanketAttachmentRef[]>([]);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [openingPath, setOpeningPath] = useState<string | null>(null);
  const [lines, setLines] = useState<LineDraft[]>([newDraftLine()]);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const row = await fetchBlanketOrder(id);
      if (!row) {
        setError('Blanket order not found.');
        setOrder(null);
        return;
      }
      setOrder(row);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load blanket order.');
      setOrder(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    void loadPoMappingContext()
      .then((ctx) => {
        if (cancelled) return;
        setCustomers(ctx.customers);
        const packing = new Map<string, string>();
        setItemOptions(
          ctx.itemOptions.map((item) => {
            const code = item.code.trim().toUpperCase();
            packing.set(code, item.packing ?? '');
            return {
              value: item.code,
              label: item.code,
              description: item.description,
              inventory: item.inventory,
              uom: item.uom,
              packing: item.packing,
              blocked: item.is_blocked,
            };
          }),
        );
        setPackingByCode(packing);
        setCtxReady(true);
      })
      .catch(() => {
        if (!cancelled) setCtxReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useDemBlanketOrdersRealtime({
    enabled: Boolean(id) && !editing,
    filter: id ? `id=eq.${id}` : undefined,
    onChange: () => {
      void load();
    },
  });

  const beginEdit = () => {
    if (!order) return;
    setCustomerCode(order.customer_code);
    setPoNumber(order.po_number);
    setNotes(order.notes ?? '');
    setAttachments(order.po_attachments ?? []);
    setPendingFiles([]);
    setLines(linesFromOrder(order, packingByCode));
    setEditing(true);
    setHeaderOpen(true);
    setLinesOpen(true);
    setError(null);
  };

  const cancelEdit = () => {
    setEditing(false);
    setPendingFiles([]);
    setError(null);
  };

  const openAttachment = async (ref: DemBlanketAttachmentRef) => {
    setOpeningPath(ref.path);
    setError(null);
    try {
      const url = await getBlanketPoAttachmentSignedUrl(ref.path);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not open attachment.');
    } finally {
      setOpeningPath(null);
    }
  };

  const selectedCustomer = useMemo(() => {
    const code = (editing ? customerCode : order?.customer_code ?? '').trim();
    return customers.find((c) => c.code === code) ?? null;
  }, [customers, customerCode, editing, order?.customer_code]);

  const customerName = editing
    ? selectedCustomer?.name ?? order?.customer_name ?? ''
    : order?.customer_name || selectedCustomer?.name || '';

  const customerAddress = selectedCustomer
    ? formatDemCustomerAddressDisplay(selectedCustomer)
    : '';

  const customerOptions: DemTypeaheadOption[] = useMemo(
    () =>
      customers.map((c) => ({
        value: c.code,
        label: c.code,
        customerName: c.name,
        address: c.address,
        address2: c.address_2,
        postal: c.postal,
        blocked: Boolean(c.is_blocked),
      })),
    [customers],
  );

  const updateLine = (key: string, patch: Partial<LineDraft>) => {
    setLines((prev) => prev.map((line) => (line.key === key ? { ...line, ...patch } : line)));
  };

  const commitItem = (key: string, code: string) => {
    const opt = itemOptions.find((o) => o.value === code);
    updateLine(key, {
      itemCode: code,
      description: opt?.description ?? '',
      packing: opt?.packing ?? '',
      uom: opt?.uom || 'PCS',
    });
  };

  const toggleStatus = async () => {
    if (!order || editing) return;
    const next = order.status === 'Open' ? 'Closed' : 'Open';
    setBusy(true);
    setError(null);
    try {
      await setBlanketOrderStatus(order.id, next);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not update status.');
    } finally {
      setBusy(false);
    }
  };

  const onSave = async () => {
    if (!order) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await updateBlanketOrder({
        id: order.id,
        customer_code: customerCode,
        customer_name: selectedCustomer?.name ?? customerName,
        po_number: poNumber,
        notes,
        lines: lines.map((line) => ({
          id: line.id,
          item_code: line.itemCode,
          description: line.description,
          ordered_qty: Number(line.qty),
          uom: line.uom,
          unit_price: null,
        })),
      });
      const uploaded: DemBlanketAttachmentRef[] = [];
      for (const file of pendingFiles) {
        uploaded.push(await uploadBlanketPoAttachment(order.id, file));
      }
      const nextAttachments = [...attachments, ...uploaded];
      const prevPaths = new Set((order.po_attachments ?? []).map((a) => a.path));
      const nextPaths = new Set(nextAttachments.map((a) => a.path));
      for (const prev of order.po_attachments ?? []) {
        if (!nextPaths.has(prev.path)) {
          try {
            await removeBlanketPoAttachmentFile(prev.path);
          } catch {
            /* keep going — metadata will drop the ref */
          }
        }
      }
      if (
        uploaded.length > 0 ||
        [...prevPaths].some((p) => !nextPaths.has(p)) ||
        nextAttachments.length !== (order.po_attachments ?? []).length
      ) {
        await setBlanketPoAttachments(order.id, nextAttachments);
      }
      const reloaded = await fetchBlanketOrder(order.id);
      setOrder(reloaded ?? { ...updated, po_attachments: nextAttachments });
      setPendingFiles([]);
      setEditing(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save blanket order.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="delivery-page">
        <div className="empty-state">Loading…</div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="delivery-page delivery-page--floating">
        <Link to="/dem/blanket-orders" className="btn-secondary delivery-page__back">
          Back to list
        </Link>
        <div className="alert alert-error">{error ?? 'Not found.'}</div>
      </div>
    );
  }

  const overDelivered = (order.lines ?? []).some((line) => line.outstanding_qty < 0);
  const viewCode = order.customer_code;
  const viewName = order.customer_name || '—';
  const viewPo = order.po_number;
  const viewNotes = order.notes?.trim() || '';
  const statusPillClass =
    order.status === 'Completed'
      ? 'delivery-status-pill--done'
      : order.status === 'Closed'
        ? 'delivery-status-pill--neutral'
        : 'delivery-status-pill--active';

  return (
    <div className="delivery-page delivery-page--form delivery-page--floating">
      <div className="dem-floating-page-actions dem-blanket-detail__toolbar">
        <div className="dem-blanket-detail__toolbar-start">
          <Link to="/dem/blanket-orders" className="btn-secondary">
            Back to list
          </Link>
          <span className={`delivery-status-pill ${statusPillClass}`}>{order.status}</span>
        </div>
        {editing ? null : (
          <div className="dem-floating-page-actions__btns">
            <button
              type="button"
              className="btn-secondary"
              disabled={busy || !ctxReady}
              onClick={beginEdit}
            >
              Edit
            </button>
            {order.status !== 'Completed' ? (
              <button
                type="button"
                className="btn-secondary"
                disabled={busy}
                onClick={() => void toggleStatus()}
              >
                {busy
                  ? 'Updating…'
                  : order.status === 'Open'
                    ? 'Close blanket'
                    : 'Reopen blanket'}
              </button>
            ) : null}
          </div>
        )}
      </div>

      {error ? <div className="alert alert-error">{error}</div> : null}
      {overDelivered ? (
        <div className="alert alert-error">
          One or more lines are over-delivered (outstanding below zero).
        </div>
      ) : null}

      <div className="dem-blanket-detail">
        <div className="dem-blanket-detail__main">
          <CollapseSection
            title="Header"
            open={headerOpen}
            onToggle={() => setHeaderOpen((v) => !v)}
          >
            <div className="dem-blanket-form__row dem-blanket-form__row--2">
              <label className="field">
                <span className="field-label">Customer no.{editing ? ' *' : ''}</span>
                {editing ? (
                  <DemTypeahead
                    value={customerCode}
                    onChange={setCustomerCode}
                    onCommit={setCustomerCode}
                    options={customerOptions}
                    placeholder="Search customer code or name"
                    tableMode="customer"
                    disabled={saving}
                    inputClassName="field-input"
                  />
                ) : (
                  <input className="field-input dem-blanket-form__readonly" value={viewCode} readOnly tabIndex={-1} />
                )}
              </label>
              <label className="field">
                <span className="field-label">Customer name</span>
                <input
                  className="field-input dem-blanket-form__readonly"
                  value={editing ? customerName : viewName}
                  readOnly
                  tabIndex={-1}
                  placeholder="—"
                />
              </label>
            </div>

            <label className="field">
              <span className="field-label">Address</span>
              <textarea
                className="field-input dem-blanket-form__readonly"
                rows={2}
                value={customerAddress || (editing ? '' : '—')}
                readOnly
                tabIndex={-1}
                placeholder="—"
              />
            </label>

            <label className="field">
              <span className="field-label">PO number{editing ? ' *' : ''}</span>
              {editing ? (
                <input
                  className="field-input"
                  value={poNumber}
                  onChange={(e) => setPoNumber(e.target.value)}
                  disabled={saving}
                />
              ) : (
                <input className="field-input dem-blanket-form__readonly" value={viewPo} readOnly tabIndex={-1} />
              )}
            </label>
          </CollapseSection>

          <CollapseSection title="Lines" open={linesOpen} onToggle={() => setLinesOpen((v) => !v)}>
            {editing ? (
              <div className="dem-blanket-form__lines-toolbar">
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  disabled={saving}
                  onClick={() => setLines((prev) => [...prev, newDraftLine()])}
                >
                  Add line
                </button>
              </div>
            ) : null}

            {editing ? (
              <div className="dem-blanket-lines">
                {lines.map((line, index) => {
                  const locked = line.deliveredQty > 0;
                  return (
                    <div key={line.key} className="dem-blanket-lines__row dem-blanket-lines__row--detail-edit">
                      <span className="dem-blanket-lines__idx">{index + 1}</span>
                      <label className="field">
                        <span className="field-label">Item *</span>
                        {locked ? (
                          <input
                            className="field-input dem-blanket-form__readonly"
                            value={line.itemCode}
                            readOnly
                            tabIndex={-1}
                          />
                        ) : (
                          <DemTypeahead
                            value={line.itemCode}
                            onChange={(code) =>
                              updateLine(line.key, {
                                itemCode: code,
                                ...(code.trim()
                                  ? {}
                                  : { description: '', packing: '', uom: 'PCS' }),
                              })
                            }
                            onCommit={(code) => commitItem(line.key, code)}
                            options={itemOptions}
                            placeholder="Item code"
                            tableMode="item"
                            disabled={saving}
                            inputClassName="field-input"
                          />
                        )}
                      </label>
                      <label className="field dem-blanket-lines__desc">
                        <span className="field-label">Description</span>
                        <input
                          className="field-input dem-blanket-form__readonly"
                          value={line.description}
                          readOnly
                          tabIndex={-1}
                          placeholder="—"
                        />
                      </label>
                      <label className="field">
                        <span className="field-label">Packing size</span>
                        <input
                          className="field-input dem-blanket-form__readonly"
                          value={line.packing}
                          readOnly
                          tabIndex={-1}
                          placeholder="—"
                        />
                      </label>
                      <label className="field">
                        <span className="field-label">Qty *</span>
                        <input
                          className="field-input"
                          type="number"
                          min={locked ? line.deliveredQty : 0}
                          step="any"
                          value={line.qty}
                          onChange={(e) => updateLine(line.key, { qty: e.target.value })}
                          disabled={saving}
                        />
                        {locked ? (
                          <span className="delivery-form-card__hint">
                            Delivered {formatQty(line.deliveredQty)}
                          </span>
                        ) : null}
                      </label>
                      <label className="field">
                        <span className="field-label">UOM</span>
                        <input
                          className="field-input dem-blanket-form__readonly"
                          value={line.uom}
                          readOnly
                          tabIndex={-1}
                        />
                      </label>
                      <button
                        type="button"
                        className="btn-secondary btn-sm"
                        disabled={saving || locked || lines.length <= 1}
                        title={locked ? 'Cannot remove a line with deliveries' : undefined}
                        onClick={() => setLines((prev) => prev.filter((l) => l.key !== line.key))}
                      >
                        Remove
                      </button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="dem-blanket-detail-table-wrap">
                <table className="dem-blanket-detail-table">
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th>Description</th>
                      <th>Packing size</th>
                      <th>UOM</th>
                      <th>Ordered</th>
                      <th>Delivered</th>
                      <th>Outstanding</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(order.lines ?? []).map((line) => (
                      <tr key={line.id} className={line.outstanding_qty < 0 ? 'is-over' : undefined}>
                        <td>{line.item_code}</td>
                        <td>{line.description || '—'}</td>
                        <td>
                          {packingByCode.get(line.item_code.trim().toUpperCase()) || '—'}
                        </td>
                        <td>{line.uom}</td>
                        <td>{formatQty(line.ordered_qty)}</td>
                        <td>{formatQty(line.delivered_qty)}</td>
                        <td>{formatQty(line.outstanding_qty)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CollapseSection>

          {editing ? (
            <div className="dem-blanket-form__actions">
              <button type="button" className="btn-secondary" disabled={saving} onClick={cancelEdit}>
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                disabled={saving}
                onClick={() => void onSave()}
              >
                {saving ? 'Saving…' : 'Save blanket'}
              </button>
            </div>
          ) : null}
        </div>

        <aside className="dem-blanket-detail__aside">
          <section className="dem-blanket-detail__side-card">
            <h2 className="dem-blanket-detail__side-title">PO attachment</h2>
            {editing ? (
              <>
                <label className="btn-secondary btn-sm dem-blanket-form__upload-btn">
                  Add files
                  <input
                    type="file"
                    multiple
                    accept="application/pdf,image/*"
                    hidden
                    disabled={saving}
                    onChange={(e) => {
                      const files = e.target.files ? Array.from(e.target.files) : [];
                      if (files.length) setPendingFiles((prev) => [...prev, ...files]);
                      e.target.value = '';
                    }}
                  />
                </label>
                <ul className="dem-blanket-form__file-list">
                  {attachments.map((ref) => (
                    <li key={ref.path}>
                      <button
                        type="button"
                        className="puf-form__file-link"
                        disabled={openingPath === ref.path}
                        onClick={() => void openAttachment(ref)}
                      >
                        {ref.fileName}
                      </button>
                      <button
                        type="button"
                        className="btn-secondary btn-sm"
                        disabled={saving}
                        onClick={() =>
                          setAttachments((prev) => prev.filter((a) => a.path !== ref.path))
                        }
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                  {pendingFiles.map((file, index) => (
                    <li key={`pending-${file.name}-${index}`}>
                      <span>{file.name} (pending)</span>
                      <button
                        type="button"
                        className="btn-secondary btn-sm"
                        disabled={saving}
                        onClick={() =>
                          setPendingFiles((prev) => prev.filter((_, i) => i !== index))
                        }
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                  {attachments.length === 0 && pendingFiles.length === 0 ? (
                    <li className="delivery-form-card__hint">No attachments</li>
                  ) : null}
                </ul>
              </>
            ) : (order.po_attachments ?? []).length > 0 ? (
              <ul className="dem-blanket-form__file-list">
                {(order.po_attachments ?? []).map((ref) => (
                  <li key={ref.path}>
                    <button
                      type="button"
                      className="puf-form__file-link"
                      disabled={openingPath === ref.path}
                      onClick={() => void openAttachment(ref)}
                    >
                      {openingPath === ref.path ? 'Opening…' : ref.fileName}
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="delivery-form-card__hint">No PO file attached.</p>
            )}
          </section>

          <section className="dem-blanket-detail__side-card">
            <h2 className="dem-blanket-detail__side-title">Notes</h2>
            {editing ? (
              <textarea
                className="field-input"
                rows={4}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional notes"
                disabled={saving}
              />
            ) : viewNotes ? (
              <p className="dem-blanket-detail__notes-text">{viewNotes}</p>
            ) : (
              <p className="delivery-form-card__hint">No notes.</p>
            )}
          </section>

          <section className="dem-blanket-detail__side-card">
            <h2 className="dem-blanket-detail__side-title">Delivery history</h2>
            {(order.movements ?? []).length === 0 ? (
              <p className="delivery-form-card__hint">
                No deductions yet. Export a DEM order with PO {order.po_number} to deduct.
              </p>
            ) : (
              <div className="dem-blanket-detail__history">
                {(order.movements ?? []).map((move) => (
                  <div key={move.id} className="dem-blanket-detail__history-item">
                    <div className="dem-blanket-detail__history-top">
                      <strong>{formatQty(move.qty)}</strong>
                      <span>{move.item_code || '—'}</span>
                    </div>
                    {move.description ? (
                      <div className="dem-blanket-detail__history-desc">{move.description}</div>
                    ) : null}
                    <div className="dem-blanket-detail__history-meta">
                      <span>{formatWhen(move.created_at)}</span>
                      <span>Order {move.dem_order_id}</span>
                      <span>{move.created_by || '—'}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </aside>
      </div>
    </div>
  );
}
